// Idempotency guard for POST /cart/add.js.
//
// The cart-page/drawer fetches are safe to blind-retry because they are
// idempotent (cart/change.js sets an ABSOLUTE quantity, cart/update.js
// overwrites, section re-render is a GET) — that is the assumption
// cart-resilience.js's cartFetch is built on. /cart/add.js is not: it
// INCREMENTS. Replaying an add whose response was lost — the exact iOS
// Facebook/Instagram in-app-webview abort we retry for — silently adds the
// shopper's items a second time.
//
// So an add is never blind-retried. On a transient failure we first ask the
// server what actually happened (GET /cart.js, which IS idempotent) and compare
// it to a snapshot of the cart taken before the POST:
//
//   observed == before + intended  -> the add landed; the response was just lost
//   observed == before             -> the add never landed; safe to re-POST
//   anything else                  -> ambiguous; do NOT re-POST, surface an error
//
// The snapshot is PREFETCHED (page idle, pointerdown on an add button, after any
// cart update) so the happy path pays no extra round trip. If no fresh snapshot
// exists when an add fails, we cannot prove a re-POST is safe, so we do not
// retry at all and let the caller surface the failure. Resilience is a bonus;
// correctness never depends on it.

const SNAPSHOT_MAX_AGE_MS = 30000;

let snapshot = null; // { at: number, lines: { [variantId]: quantity } }

function now() {
  return Date.now();
}

function cartRoute(path) {
  const root =
    (typeof window !== 'undefined' &&
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root) ||
    '/';
  return `${root}${path}`;
}

// Every guard request is bounded (deps.timeoutMs, default 15s; 0 disables). iOS
// in-app webviews sometimes leave a request pending forever instead of rejecting
// it, and an add that never settles never reaches the reconcile below, so the
// caller's buttons stay disabled until reload (#533). A timed-out request is
// aborted and takes the same path as a "Load failed" abort: read the cart back
// and decide from the snapshot whether the add landed, never a blind re-POST.
const CART_ADD_TIMEOUT_MS = 15000;

function cartAddTimeoutMs(deps) {
  return deps && deps.timeoutMs != null ? deps.timeoutMs : CART_ADD_TIMEOUT_MS;
}

// Reads the body once; the response's json() and text() then resolve from the
// buffered copy.
async function bufferGuardResponse(response) {
  if (!response || typeof response !== 'object') return response;
  let body;
  let parsed;
  if (typeof response.text === 'function') {
    body = await response.text();
  } else if (typeof response.json === 'function') {
    parsed = await response.json();
    body = JSON.stringify(parsed);
  } else {
    return response;
  }
  const text = async () => body;
  const json = async () => (parsed !== undefined ? parsed : JSON.parse(body));
  try {
    // Same object back, so callers keep its identity, headers and status.
    response.text = text;
    response.json = json;
    return response;
  } catch (error) {
    return { ok: response.ok, status: response.status, headers: response.headers, text, json };
  }
}

function boundedFetch(doFetch, timeoutMs) {
  // A timeout we cannot enforce with an abort would leave the add live while
  // the guard reconciles and may re-POST, so without AbortController requests
  // stay unbounded, as they were before.
  if (!(timeoutMs > 0) || typeof AbortController !== 'function') return doFetch;
  return (url, options) => {
    const controller = new AbortController();
    const callerSignal = options && options.signal;
    let forwardAbort = null;
    // Keep the caller's own abort working. AbortSignal.any is too new for the
    // iOS versions we still serve, so forward it by hand.
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort();
      else {
        forwardAbort = () => controller.abort();
        callerSignal.addEventListener('abort', forwardAbort, { once: true });
      }
    }
    const requestOptions = Object.assign({}, options, { signal: controller.signal });

    let okHeaders = false;
    let headerStatus = 0;
    let timer;
    const deadline = new Promise((resolve, reject) => {
      timer = setTimeout(() => {
        const error = new Error(`Request timed out after ${timeoutMs}ms`);
        error.name = 'TimeoutError';
        // The server already answered OK and only the body stalled: the request
        // took effect, so a caller must not treat it as failed.
        error.responseOk = okHeaders;
        // Non-OK headers (422 sold out) are just as final: keep the status so a
        // caller can tell a refused add from one that may still land.
        if (headerStatus) error.responseStatus = headerStatus;
        // Reject before aborting: the abort rejects the request synchronously, and
        // the race must report the timeout, not a generic AbortError.
        reject(error);
        controller.abort();
      }, timeoutMs);
    });

    // The deadline covers the body too: headers can arrive and the body still
    // stall. Reading it here keeps a body timeout inside the caller's own error
    // handling (retry, reconcile, telemetry) instead of surfacing later.
    const request = Promise.resolve(doFetch(url, requestOptions)).then((response) => {
      okHeaders = !!(response && response.ok);
      headerStatus = (response && response.status) || 0;
      return Promise.resolve(bufferGuardResponse(response)).catch((bodyError) => {
        // Any body failure after OK headers still means the request took effect.
        if (bodyError && typeof bodyError === 'object') {
          if (okHeaders) bodyError.responseOk = true;
          if (headerStatus) bodyError.responseStatus = headerStatus;
        }
        throw bodyError;
      });
    });
    return Promise.race([request, deadline]).finally(() => {
      clearTimeout(timer);
      if (forwardAbort) callerSignal.removeEventListener('abort', forwardAbort);
    });
  };
}

function resolveFetch(deps) {
  const doFetch = (deps && deps.fetch) || ((url, options) => fetch(url, options));
  return boundedFetch(doFetch, cartAddTimeoutMs(deps));
}

// variant_id -> total quantity, summed across lines (the same variant can appear
// on more than one line when line-item properties differ, e.g. a freebie).
function lineQuantities(cart) {
  const lines = (cart && cart.items) || [];
  return lines.reduce((acc, line) => {
    const id = String(line.variant_id != null ? line.variant_id : line.id);
    acc[id] = (acc[id] || 0) + (line.quantity || 0);
    return acc;
  }, {});
}

async function readCart(deps) {
  const doFetch = resolveFetch(deps);
  const response = await doFetch(cartRoute('cart.js'), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response || !response.ok) {
    throw new Error(`cart read failed: HTTP ${response ? response.status : 'no response'}`);
  }
  return response.json();
}

// Refresh the cached snapshot. Best-effort: a failure here must never break an
// add — it just means the next failed add won't be able to prove a retry is safe.
async function primeCartSnapshot(deps) {
  try {
    const cart = await readCart(deps);
    snapshot = { at: now(), lines: lineQuantities(cart) };
    return snapshot;
  } catch (error) {
    return null;
  }
}

function freshSnapshot() {
  if (!snapshot) return null;
  if (now() - snapshot.at > SNAPSHOT_MAX_AGE_MS) return null;
  return snapshot.lines;
}

// After an add we KNOW succeeded, roll the snapshot forward arithmetically rather
// than re-reading /cart.js — re-reading would put an extra request on the happy path
// of the busiest surface in the store. The prefetch triggers (cart update, pointerdown,
// load) still re-anchor against the server, so drift is short-lived and self-correcting.
function advanceSnapshot(items) {
  if (!snapshot || !items) return;
  const lines = Object.assign({}, snapshot.lines);
  items.forEach((item) => {
    lines[item.id] = (lines[item.id] || 0) + item.quantity;
  });
  snapshot = { at: now(), lines };
}

// The intended add, read back off the request we're about to send: FormData on
// the Dawn PDP path, a JSON items array on the funnel paths. Returns null when
// the payload can't be read — which disables the retry rather than guessing.
function parseAddItems(options) {
  const body = options && options.body;
  if (!body) return null;

  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const id = body.get('id');
    if (!id) return null;
    const quantity = parseInt(body.get('quantity'), 10);
    return [{ id: String(id), quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 }];
  }

  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const items = Array.isArray(parsed.items)
        ? parsed.items
        : parsed && parsed.id != null
          ? [parsed]
          : null;
      if (!items || !items.length) return null;
      return items.map((item) => ({
        id: String(item.id),
        quantity: item.quantity == null ? 1 : item.quantity,
      }));
    } catch (error) {
      return null;
    }
  }

  return null;
}

// 'applied' | 'not-applied' | 'ambiguous'. Every intended item must tell the
// same story; a partial or unexpected delta is ambiguous and must not be retried.
// Cart quantities are per variant, so intended items for the same variant (different
// properties or selling plans) are summed before comparing.
function reconcile(items, before, after) {
  const intended = {};
  items.forEach((item) => {
    intended[item.id] = (intended[item.id] || 0) + item.quantity;
  });
  const ids = Object.keys(intended);
  let applied = 0;
  let untouched = 0;

  ids.forEach((id) => {
    const was = before[id] || 0;
    const is = after[id] || 0;
    if (is === was + intended[id]) applied += 1;
    else if (is === was) untouched += 1;
  });

  if (applied === ids.length) return 'applied';
  if (untouched === ids.length) return 'not-applied';
  return 'ambiguous';
}

// Mirrors NttUtils.fetchWithRetry's give-up telemetry: sendBeacon + send_instantly,
// because the abort that killed the fetch is usually the app tearing the page down,
// which would also cancel PostHog's default async request (#112).
function reportGiveUp(url, step, reason, error) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.posthog &&
      typeof window.posthog.capture === 'function'
    ) {
      window.posthog.capture(
        'fetch_failed',
        {
          url: String(url),
          step: step || 'cart-add',
          reason,
          message: (error && error.message) || String(error),
          path: (window.location && window.location.pathname) || '',
        },
        { transport: 'sendBeacon', send_instantly: true }
      );
    }
  } catch (telemetryError) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('fetch_failed capture skipped', telemetryError);
    }
  }
}

// Success-path telemetry. Without this the guard is unobservable: it only ever
// emitted `fetch_failed` on give-up, so a guard that prevented a double-add and a
// guard that never loaded produced byte-identical PostHog data — there was no
// metric by which the INV-030 fix could be shown to work. `cart_add_guard_reconciled`
// fires only when the guard actually engaged (a transient failure forced a
// reconcile), so its volume is the rate of recovered adds, and `verdict: 'applied'`
// is specifically the count of double-adds prevented.
//
// Not sendBeacon: unlike reportGiveUp, these fire on paths where the page survives
// (the shopper continues), so the default async transport is correct and keeps this
// off the teardown-critical path.
function reportGuardOutcome(url, step, verdict) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.posthog &&
      typeof window.posthog.capture === 'function'
    ) {
      window.posthog.capture('cart_add_guard_reconciled', {
        url: String(url),
        step: step || 'cart-add',
        verdict,
        path: (window.location && window.location.pathname) || '',
      });
    }
  } catch (telemetryError) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('cart_add_guard_reconciled capture skipped', telemetryError);
    }
  }
}

// Non-OK 4xx headers arrived before the body failed: Shopify refused the add.
function isRefusal(error) {
  if (!error || error.responseOk) return false;
  const status = error.responseStatus;
  return status >= 400 && status < 500;
}

// Resolves { response, alreadyApplied }. `alreadyApplied: true` means the add DID
// land server-side but its response was lost — there is no add response to hand
// back, so the caller must refresh cart state rather than read `response`.
// Rejects when the add did not land and could not be safely recovered.
async function cartAdd(url, options, deps) {
  const d = deps || {};
  const doFetch = resolveFetch(d);
  const step = d.step || 'cart-add';
  const items = parseAddItems(options);
  const before = d.snapshot || freshSnapshot();

  let response = null;
  let error = null;
  try {
    response = await doFetch(url, options);
  } catch (thrown) {
    error = thrown;
  }

  if (response && response.ok) {
    advanceSnapshot(items);
    return { response, alreadyApplied: false };
  }
  // OK headers arrived and only the body stalled: the add committed, so there is
  // nothing to reconcile and nothing to retry.
  if (error && error.responseOk) {
    advanceSnapshot(items);
    reportGuardOutcome(url, step, 'applied');
    return { response: null, alreadyApplied: true };
  }
  // A definitive answer (422 sold-out, 404) is not transient — hand it back so the
  // caller can read the body and message the shopper.
  if (!error && response && response.status < 500) {
    return { response, alreadyApplied: false };
  }
  // The same answer whose body then stalled: still a refusal, so it must not be
  // reconciled or flagged as possibly applied. There is no body to hand back.
  if (isRefusal(error)) {
    reportGiveUp(url, step, 'refused-body-lost', error);
    throw error;
  }

  const transient = error || new Error(`HTTP ${response ? response.status : 'no response'}`);

  // An abort stops the browser waiting, not a request Shopify already has, so a
  // timed-out add may still land. Mark it before any exit below so the caller
  // holds off on its own retry whichever way this ends.
  if (transient.name === 'TimeoutError') transient.mayHaveApplied = true;

  // No intended-items payload or no fresh snapshot => we cannot prove a re-POST
  // wouldn't double-add. Fail loudly instead of risking the shopper's cart.
  if (!items || !before) {
    reportGiveUp(url, step, items ? 'no-snapshot' : 'unparseable-payload', transient);
    throw transient;
  }

  let after;
  try {
    after = lineQuantities(await readCart(d));
  } catch (readError) {
    reportGiveUp(url, step, 'cart-read-failed', transient);
    throw transient;
  }

  const verdict = reconcile(items, before, after);

  if (verdict === 'applied') {
    // The add landed and only its response was lost. This is the double-add the
    // old fetchWithRetry path would have created — count it.
    snapshot = { at: now(), lines: after };
    reportGuardOutcome(url, step, 'applied');
    return { response: null, alreadyApplied: true };
  }

  if (verdict === 'ambiguous') {
    reportGuardOutcome(url, step, 'ambiguous');
    reportGiveUp(url, step, 'ambiguous-cart-state', transient);
    throw transient;
  }

  // A timed-out add can still land after this cart read. Never replay it; let
  // the shopper retry once it has had time to settle.
  if (transient.name === 'TimeoutError') {
    reportGiveUp(url, step, 'timeout-not-replayed', transient);
    throw transient;
  }

  // 'not-applied': the server never took the add, so re-POSTing cannot double it.
  reportGuardOutcome(url, step, 'not-applied');
  let retryResponse = null;
  let retryError = null;
  try {
    retryResponse = await doFetch(url, options);
  } catch (thrown) {
    retryError = thrown;
  }

  if (retryResponse && retryResponse.ok) {
    advanceSnapshot(items);
    return { response: retryResponse, alreadyApplied: false };
  }
  if (retryError && retryError.responseOk) {
    advanceSnapshot(items);
    reportGuardOutcome(url, step, 'applied');
    return { response: null, alreadyApplied: true };
  }
  if (!retryError && retryResponse && retryResponse.status < 500) {
    return { response: retryResponse, alreadyApplied: false };
  }
  if (isRefusal(retryError)) {
    reportGiveUp(url, step, 'refused-body-lost', retryError);
    throw retryError;
  }

  const giveUp =
    retryError || new Error(`HTTP ${retryResponse ? retryResponse.status : 'no response'}`);

  // A retry that threw (timed out, stalled body, aborted) may still have
  // committed. Read the cart once more so the caller never offers a retry for
  // an add that already landed.
  if (retryError) {
    try {
      const final = lineQuantities(await readCart(d));
      if (reconcile(items, after, final) === 'applied') {
        snapshot = { at: now(), lines: final };
        reportGuardOutcome(url, step, 'applied');
        return { response: null, alreadyApplied: true };
      }
    } catch (readError) {
      // Fall through to the original failure.
    }
  }

  // A timed-out retry can land after the final read just like a first attempt.
  if (giveUp.name === 'TimeoutError') giveUp.mayHaveApplied = true;
  reportGiveUp(url, step, 'retry-failed', giveUp);
  throw giveUp;
}

// Cart state plus freshly rendered sections, shaped like a /cart/add.js response
// so Dawn's cart.renderContents(parsedState) can consume it after an
// `alreadyApplied` recovery (where the real add response was lost).
async function cartStateWithSections(sectionIds, deps) {
  const d = deps || {};
  let cart;
  try {
    cart = await readCart(d);
  } catch (error) {
    // The add already landed; only this refresh failed. Mark it so the caller
    // does not offer a retry that would add the item again.
    if (error && typeof error === 'object') error.addApplied = true;
    throw error;
  }
  if (!sectionIds || !sectionIds.length) return cart;

  // The cart drawer and notification render only from sections, so without
  // them there is nothing to render: fail marked addApplied, like the read.
  let sections;
  try {
    const doFetch = resolveFetch(d);
    const pathname = (typeof window !== 'undefined' && window.location.pathname) || '/';
    const response = await doFetch(`${pathname}?sections=${sectionIds.join(',')}`);
    if (!response || !response.ok) {
      throw new Error(`sections refresh failed: HTTP ${response ? response.status : 'no response'}`);
    }
    sections = await response.json();
  } catch (error) {
    if (error && typeof error === 'object') error.addApplied = true;
    throw error;
  }
  return Object.assign({}, cart, { sections });
}

// Prefetch the snapshot so the happy path never pays for the guard: once on idle,
// again the moment the shopper reaches for an add button, and after every cart
// mutation the theme publishes.
function installSnapshotPrefetch() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const prime = () => primeCartSnapshot({});

  if (document.readyState === 'complete') prime();
  else window.addEventListener('load', prime, { once: true });

  // pointerdown fires ~100ms before the click completes and the form submits —
  // enough head start that the snapshot is warm by the time the add POSTs.
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (target && typeof target.closest === 'function' && target.closest('[type="submit"], .product-form__submit, [data-add-to-cart]')) {
        prime();
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener('cart:refresh', prime);
  subscribeToCartUpdates(prime);
}

// Dawn publishes cart updates over its own PubSub, which isn't a DOM event.
function subscribeToCartUpdates(prime) {
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, prime);
    }
  } catch (error) {
    // PubSub not loaded on this template — the load/pointerdown primes still cover us.
  }
}

if (typeof window !== 'undefined') {
  window.CartAddGuard = window.CartAddGuard ?? {};
  window.CartAddGuard.cartAdd = cartAdd;
  window.CartAddGuard.primeCartSnapshot = primeCartSnapshot;
  window.CartAddGuard.cartStateWithSections = cartStateWithSections;
  installSnapshotPrefetch();
}

// Node-only test export. Skipped under <script> in the theme (process undefined).
if (
  typeof module === 'object' && module !== null &&
  typeof module.exports === 'object' &&
  typeof process !== 'undefined' && process.versions != null && process.versions.node != null
) {
  module.exports = {
    cartAdd,
    primeCartSnapshot,
    cartStateWithSections,
    parseAddItems,
    reconcile,
    lineQuantities,
    __resetSnapshot: () => {
      snapshot = null;
    },
  };
}
